<script setup lang="ts">
import CloseIconDefault from '@assets/icons/close-round.svg';
import SpinnerIcon from '@assets/icons/spinner.svg?component';
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';

type Props = {
  title?: string
  width?: number
  classNames?: string
  loading?: boolean
  closeOnOverlayClick?: boolean
  position?: 'left' | 'right'
};

const props = withDefaults(defineProps<Props>(), {
  title: '',
  width: 491,
  classNames: '',
  loading: false,
  closeOnOverlayClick: true,
  position: 'left',
});

const emit = defineEmits<{ 'change': [value: boolean] }>();

const buttonWrapper = ref<HTMLElement | null>(null);
const isVisible = ref(false);

const open = () => isVisible.value = true;
const close = () => isVisible.value = false;

const onEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    close();
  }
};

watch(isVisible, (value) => {
  emit('change', value);
});

onMounted(() => {
  buttonWrapper.value?.children[0]?.addEventListener('click', open);
  window.addEventListener('keydown', onEscape);
});

onBeforeUnmount(() => {
  buttonWrapper.value?.children[0]?.removeEventListener('click', open);
  window.removeEventListener('keydown', onEscape);
});

defineExpose({ open, close });
</script>

<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 z-[9999] bg-black/40"
    :class="{ 'cursor-default': !closeOnOverlayClick }"
    @click="closeOnOverlayClick && close()"
  />

  <Transition :name="position === 'right' ? 'drawer-right' : 'drawer'">
    <div
      v-if="isVisible"
      class="base-drawer__panel fixed inset-y-0 z-[9999] flex w-full flex-col overflow-auto bg-white p-30 shadow-2xl"
      :class="[classNames, position === 'right' ? 'right-0' : 'left-0']"
      :style="{ '--base-drawer-max-width': `${width}px` }"
    >
      <button
        class="sticky top-0 z-10 flex items-center self-end justify-center -mb-24 size-24 text-warm-grey hover:text-turquoise lg:-mb-32 lg:size-32"
        type="button"
        data-testid="login-close-button"
        data-testpl="dialog-modal-close-button"
        @click="close"
      >
        <slot name="close-icon">
          <CloseIconDefault class="size-22 lg:size-24" />
        </slot>
      </button>

      <h1
        v-if="title"
        class="font-semibold font-inter-tight text-20 text-off-black-500"
      >
        {{ title }}
      </h1>

      <div class="mt-24 md:mt-36 grow">
        <div
          v-if="loading"
          class="flex items-center justify-center h-full"
        >
          <SpinnerIcon class="size-48" />
        </div>

        <slot
          v-else
          name="content"
        />
      </div>
    </div>
  </Transition>

  <div ref="buttonWrapper">
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.base-drawer__panel {
  max-width: 100%;

  @media (min-width: 1024px) {
    max-width: var(--base-drawer-max-width, 491px);
  }
}
</style>
